import { Box, Button, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useTheme } from '@/contexts/ThemeContext'
import { createTextFieldStyles } from '@/components/shared/commonStyles'
import { createDatePickerPopperStyles } from '@/components/shared/commonStyles'
import TransactionTypeSelect from './TransactionTypeSelect'
import { parseLocalDate, formatLocalDate } from './utils'
import { useMemo } from 'react'
import type { ElementType } from 'react'
import type { TextFieldProps } from '@mui/material'

interface TransactionFiltersProps {
  filterDateFrom: string
  filterDateTo: string
  filterNonce: string
  filterType: string
  onDateFromChange: (date: string) => void
  onDateToChange: (date: string) => void
  onNonceChange: (nonce: string) => void
  onTypeChange: (type: string) => void
  onClear: () => void
  onApply: () => void
  StyledTextField: ElementType<TextFieldProps>
}

export default function TransactionFilters({
  filterDateFrom,
  filterDateTo,
  filterNonce,
  filterType,
  onDateFromChange,
  onDateToChange,
  onNonceChange,
  onTypeChange,
  onClear,
  onApply,
  StyledTextField,
}: TransactionFiltersProps) {
  const { colors } = useTheme()

  const datePickerPopperProps = useMemo(
    () => createDatePickerPopperStyles(colors),
    [colors]
  )

  const commonDatePickerProps = useMemo(
    () => ({
      format: 'yyyy-MM-dd' as const,
      enableAccessibleFieldDOMStructure: false,
      slots: {
        textField: StyledTextField,
      },
      slotProps: {
        textField: {
          size: 'small' as const,
          fullWidth: true,
        },
        popper: datePickerPopperProps,
      },
    }),
    [StyledTextField, datePickerPopperProps]
  )

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          color: colors.yellow,
          mb: 2,
          fontWeight: 600,
        }}
      >
        Filter Transaction History
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: '1fr 1fr 1fr 1fr',
          },
          gap: 2,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Date From"
            value={parseLocalDate(filterDateFrom)}
            onChange={(newValue) => {
              onDateFromChange(formatLocalDate(newValue))
            }}
            {...commonDatePickerProps}
          />
          <DatePicker
            label="Date To"
            value={parseLocalDate(filterDateTo)}
            onChange={(newValue) => {
              onDateToChange(formatLocalDate(newValue))
            }}
            {...commonDatePickerProps}
          />
        </LocalizationProvider>
        <TextField
          label="Nonce"
          type="number"
          value={filterNonce}
          onChange={(e) => onNonceChange(e.target.value)}
          fullWidth
          size="small"
          sx={{
            ...createTextFieldStyles(colors),
            gridColumn: {
              xs: 'span 1',
              sm: 'span 2',
              md: 'span 1',
            },
          }}
        />
        <TransactionTypeSelect
          value={filterType}
          onChange={onTypeChange}
          label="Type"
          size="small"
          sx={{
            gridColumn: {
              xs: 'span 1',
              sm: 'span 2',
              md: 'span 1',
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 2,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={onClear}
          sx={{
            borderColor: colors.text,
            color: colors.text,
            '&:hover': {
              borderColor: colors.text,
              backgroundColor: `${colors.text}20`,
            },
          }}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onApply}
          sx={{
            backgroundColor: colors.primary,
            color: colors.background,
            '&:hover': {
              backgroundColor: colors.primary,
              opacity: 0.8,
            },
          }}
        >
          Apply
        </Button>
      </Box>
    </Box>
  )
}
